import React, { useState, useEffect } from 'react';
import { X, Download, Trash2, UserPlus, Users, Mail, Phone, Search } from 'lucide-react';
import segmentService from '../../services/segmentService';

interface Lead {
  id: number;
  email: string;
  parent_name?: string;
  child_name?: string;
  phone?: string;
  child_age?: number;
  special_needs: string[];
  current_support: string[];
  timeline?: string;
  lead_score: number;
  manually_assigned: boolean;
  assigned_at: string;
  created_at: string;
}

interface Segment {
  id: number;
  name: string;
  description: string;
  active_leads: number;
  email_list_ready: boolean;
  never_contact: boolean;
}

interface SegmentLeadViewProps {
  segment: Segment;
  onClose: () => void;
  onSegmentUpdated?: () => void; // Add callback to refresh parent
}

const SegmentLeadView: React.FC<SegmentLeadViewProps> = ({ segment, onClose, onSegmentUpdated }) => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLeads, setSelectedLeads] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [scoreFilter, setScoreFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const [assignmentFilter, setAssignmentFilter] = useState<'all' | 'auto' | 'manual'>('all');
  
  const [showAddLeads, setShowAddLeads] = useState(false);
  const [availableLeads, setAvailableLeads] = useState<Lead[]>([]);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [allSegments, setAllSegments] = useState<any[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchSegmentLeads();
    fetchAllSegments();
  }, [segment.id, currentPage, searchTerm, scoreFilter, assignmentFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAllSegments = async () => {
    try {
      const segments = await segmentService.getSegments();
      setAllSegments(segments.filter(s => s.is_active && !s.never_contact && s.id !== segment.id));
    } catch (err) {
      console.error('Failed to fetch all segments:', err);
    }
  };

  const fetchSegmentLeads = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data: any = await segmentService.getSegmentLeads(segment.id, currentPage, 20);
      setLeads(data.leads || []);
      setTotalPages(data.pagination?.totalPages || 1);
    } catch (err: any) {
      console.error('Error fetching segment leads:', err);
      if (err.message?.includes('401') || err.message?.includes('unauthorized')) {
        setError('Authentication error. Please log in again.');
      } else {
        setError('Failed to load segment leads. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableLeads = async () => {
    try {
      setLoadingAvailable(true);
      const token = localStorage.getItem('chatbot_admin_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/leads`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Filter out leads already in this segment
        const currentLeadIds = leads.map(l => l.id);
        const available = data.leads.filter((lead: Lead) => !currentLeadIds.includes(lead.id));
        setAvailableLeads(available);
      }
    } catch (err) {
      console.error('Error fetching available leads:', err);
    } finally {
      setLoadingAvailable(false);
    }
  };

  const handleSelectLead = (leadId: number) => {
    setSelectedLeads(prev =>
      prev.includes(leadId)
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleSelectAll = () => {
    setSelectedLeads(
      selectedLeads.length === leads.length
        ? []
        : leads.map(lead => lead.id)
    );
  };

  const handleRemoveLeads = async () => {
    if (selectedLeads.length === 0) return;
    
    if (!window.confirm(`Remove ${selectedLeads.length} leads from this segment?`)) return;

    try {
      const result = await segmentService.removeLeadsFromSegment(segment.id, selectedLeads);
      if (result.success) {
        fetchSegmentLeads();
        setSelectedLeads([]);
        onSegmentUpdated?.(); // Refresh parent component
      } else {
        setError('Failed to remove leads');
      }
    } catch (err: any) {
      console.error('Error removing leads:', err);
      setError(err.message || 'Failed to remove leads');
    }
  };

  const handleAddLeads = async (leadIds: number[]) => {
    try {
      const result = await segmentService.assignLeadsToSegment(segment.id, leadIds);
      if (result.success) {
        fetchSegmentLeads();
        setShowAddLeads(false);
        onSegmentUpdated?.(); // Refresh parent component
      } else {
        setError(result.message || 'Failed to add leads to segment');
      }
    } catch (err: any) {
      console.error('Error adding leads:', err);
      setError(err.message || 'Failed to add leads');
    }
  };


  const handleBulkMoveToSegment = async (targetSegmentId: number) => {
    if (selectedLeads.length === 0) return;
    
    try {
      // Remove from current segment
      await segmentService.removeLeadsFromSegment(segment.id, selectedLeads);
      
      // Add to target segment
      const result = await segmentService.assignLeadsToSegment(targetSegmentId, selectedLeads);
      
      if (result.success) {
        fetchSegmentLeads();
        setSelectedLeads([]);
        onSegmentUpdated?.(); // Refresh parent component
        const targetSegment = allSegments.find(s => s.id === targetSegmentId);
        setSuccessMessage(`${selectedLeads.length} leads moved to ${targetSegment?.name || 'target segment'} successfully`);
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err: any) {
      console.error('Error moving leads:', err);
      setError(err.message || 'Failed to move leads');
    }
  };

  const handleExportSegment = async () => {
    try {
      const blob = await segmentService.exportSegment(segment.id, 'csv');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${segment.name.replace(/[^a-zA-Z0-9]/g, '_')}_leads.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error exporting segment:', err);
      setError(err.message || 'Failed to export segment');
    }
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = 
      lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.parent_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.child_name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesScore = 
      scoreFilter === 'all' ||
      (scoreFilter === 'high' && lead.lead_score >= 70) ||
      (scoreFilter === 'medium' && lead.lead_score >= 40 && lead.lead_score < 70) ||
      (scoreFilter === 'low' && lead.lead_score < 40);

    const matchesAssignment = 
      assignmentFilter === 'all' ||
      (assignmentFilter === 'manual' && lead.manually_assigned) ||
      (assignmentFilter === 'auto' && !lead.manually_assigned);

    return matchesSearch && matchesScore && matchesAssignment;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getScoreColor = (score: number) => {
    if (score >= 70) return 'text-green-600 bg-green-100';
    if (score >= 40) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  if (loading && leads.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg p-8 max-w-md">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-7xl max-h-[90vh] overflow-hidden w-full flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Users size={24} />
              {segment.name}
            </h2>
            <p className="text-gray-600">{segment.description}</p>
            <div className="flex items-center gap-4 mt-2 text-sm">
              <span className="text-gray-500">
                {filteredLeads.length} of {leads.length} leads shown
                {leads.length !== segment.active_leads && (
                  <span className="text-gray-400 ml-2">
                    ({segment.active_leads} total in segment)
                  </span>
                )}
              </span>
              {segment.email_list_ready && (
                <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">
                  Email Ready
                </span>
              )}
              {segment.never_contact && (
                <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs">
                  Do Not Contact
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        {/* Controls */}
        <div className="p-6 border-b bg-gray-50">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {/* Search */}
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search leads..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border rounded-lg text-sm w-64"
                />
              </div>

              {/* Filters */}
              <select
                value={scoreFilter}
                onChange={(e) => setScoreFilter(e.target.value as any)}
                className="px-3 py-2 border rounded-lg text-sm"
              >
                <option value="all">All Scores</option>
                <option value="high">High (70+)</option>
                <option value="medium">Medium (40-69)</option>
                <option value="low">Low (&lt;40)</option>
              </select>

              <select
                value={assignmentFilter}
                onChange={(e) => setAssignmentFilter(e.target.value as any)}
                className="px-3 py-2 border rounded-lg text-sm"
              >
                <option value="all">All Leads</option>
                <option value="auto">Auto-assigned</option>
                <option value="manual">Manually Added</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              {selectedLeads.length > 0 && (
                <>
                  <button
                    onClick={handleRemoveLeads}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-red-700"
                  >
                    <Trash2 size={16} />
                    Remove ({selectedLeads.length})
                  </button>
                  
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        handleBulkMoveToSegment(Number(e.target.value));
                        e.target.value = ''; // Reset dropdown
                      }
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    disabled={selectedLeads.length === 0}
                  >
                    <option value="">Move to segment...</option>
                    {allSegments.map(seg => (
                      <option key={seg.id} value={seg.id}>
                        {seg.name}
                      </option>
                    ))}
                  </select>
                </>
              )}
              
              <button
                onClick={() => {
                  setShowAddLeads(true);
                  fetchAvailableLeads();
                }}
                className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700"
              >
                <UserPlus size={16} />
                Add Leads
              </button>

              <button
                onClick={handleExportSegment}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
                disabled={leads.length === 0}
              >
                <Download size={16} />
                Export CSV
              </button>
            </div>
          </div>

          {selectedLeads.length > 0 && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-blue-700 text-sm">
                {selectedLeads.length} leads selected
                <button
                  onClick={() => setSelectedLeads([])}
                  className="ml-4 text-blue-600 underline"
                >
                  Clear selection
                </button>
              </p>
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700">{error}</p>
            <button 
              onClick={() => setError(null)}
              className="text-red-600 underline text-sm"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="mx-6 mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-green-600">✅</span>
                <p className="text-green-700 font-medium">{successMessage}</p>
              </div>
              <button 
                onClick={() => setSuccessMessage(null)}
                className="text-green-600 hover:text-green-800"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Leads Table */}
        <div className="flex-1 overflow-auto">
          <div className="p-6">
            {filteredLeads.length === 0 ? (
              <div className="text-center py-12">
                <Users size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No leads found</h3>
                <p className="text-gray-600">
                  {searchTerm ? 'Try adjusting your search criteria' : 'This segment has no leads yet'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-4 p-3 bg-gray-50 rounded-lg text-sm font-medium text-gray-700">
                  <div className="col-span-1">
                    <input
                      type="checkbox"
                      checked={selectedLeads.length === filteredLeads.length}
                      onChange={handleSelectAll}
                      className="rounded"
                    />
                  </div>
                  <div className="col-span-3">Contact</div>
                  <div className="col-span-2">Child</div>
                  <div className="col-span-2">Special Needs</div>
                  <div className="col-span-1">Score</div>
                  <div className="col-span-1">Timeline</div>
                  <div className="col-span-1">Assignment</div>
                  <div className="col-span-1">Added</div>
                </div>

                {/* Table Rows */}
                {filteredLeads.map(lead => (
                  <div
                    key={lead.id}
                    className={`grid grid-cols-12 gap-4 p-3 border rounded-lg hover:bg-gray-50 ${
                      selectedLeads.includes(lead.id) ? 'bg-blue-50 border-blue-200' : ''
                    }`}
                  >
                    <div className="col-span-1">
                      <input
                        type="checkbox"
                        checked={selectedLeads.includes(lead.id)}
                        onChange={() => handleSelectLead(lead.id)}
                        className="rounded"
                      />
                    </div>
                    
                    <div className="col-span-3">
                      <div className="font-medium">{lead.parent_name || 'N/A'}</div>
                      <div className="text-sm text-gray-600 flex items-center gap-1">
                        <Mail size={12} />
                        {lead.email}
                      </div>
                      {lead.phone && (
                        <div className="text-sm text-gray-600 flex items-center gap-1">
                          <Phone size={12} />
                          {lead.phone}
                        </div>
                      )}
                    </div>
                    
                    <div className="col-span-2">
                      <div className="font-medium">{lead.child_name || 'N/A'}</div>
                      {lead.child_age && (
                        <div className="text-sm text-gray-600">Age {lead.child_age}</div>
                      )}
                    </div>
                    
                    <div className="col-span-2">
                      <div className="flex flex-wrap gap-1">
                        {lead.special_needs.slice(0, 2).map((need, index) => (
                          <span
                            key={index}
                            className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs"
                          >
                            {need}
                          </span>
                        ))}
                        {lead.special_needs.length > 2 && (
                          <span className="text-xs text-gray-500">
                            +{lead.special_needs.length - 2} more
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="col-span-1">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getScoreColor(lead.lead_score)}`}>
                        {lead.lead_score}
                      </span>
                    </div>
                    
                    <div className="col-span-1">
                      <span className="text-sm">{lead.timeline || 'N/A'}</span>
                    </div>
                    
                    <div className="col-span-1">
                      <span className={`px-2 py-1 rounded text-xs ${
                        lead.manually_assigned 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {lead.manually_assigned ? 'Manual' : 'Auto'}
                      </span>
                    </div>
                    
                    <div className="col-span-1">
                      <span className="text-sm text-gray-600">
                        {formatDate(lead.assigned_at)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-6">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 border rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="px-3 py-2">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 border rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Leads Modal */}
      {showAddLeads && (
        <AddLeadsModal
          availableLeads={availableLeads}
          loading={loadingAvailable}
          onAdd={handleAddLeads}
          onCancel={() => setShowAddLeads(false)}
        />
      )}
    </div>
  );
};

// Component for adding leads to segment
const AddLeadsModal: React.FC<{
  availableLeads: Lead[];
  loading: boolean;
  onAdd: (leadIds: number[]) => void;
  onCancel: () => void;
}> = ({ availableLeads, loading, onAdd, onCancel }) => {
  const [selectedLeads, setSelectedLeads] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredLeads = availableLeads.filter(lead =>
    lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.parent_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.child_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggleLead = (leadId: number) => {
    setSelectedLeads(prev =>
      prev.includes(leadId)
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-60 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl max-h-[80vh] overflow-hidden w-full">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold">Add Leads to Segment</h3>
          <div className="mt-4">
            <input
              type="text"
              placeholder="Search leads..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
        </div>

        <div className="p-6 max-h-96 overflow-y-auto">
          {loading ? (
            <div className="text-center py-8">Loading available leads...</div>
          ) : filteredLeads.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600">No available leads found</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredLeads.map(lead => (
                <label
                  key={lead.id}
                  className="flex items-center gap-3 p-3 border rounded hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedLeads.includes(lead.id)}
                    onChange={() => handleToggleLead(lead.id)}
                    className="rounded"
                  />
                  <div className="flex-1">
                    <div className="font-medium">{lead.parent_name || lead.email}</div>
                    <div className="text-sm text-gray-600">
                      {lead.child_name} - Score: {lead.lead_score}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-6 border-t">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onAdd(selectedLeads)}
            disabled={selectedLeads.length === 0}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            Add {selectedLeads.length} Leads
          </button>
        </div>
      </div>
    </div>
  );
};

export default SegmentLeadView;